import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Microsoft.css";
import microsoftLogo from "../../assets/images/partners/microsoft.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const capabilities = [
  { icon: "☁️", name: "Microsoft Azure", sub: "Cloud Infrastructure" },
  { icon: "💼", name: "Microsoft 365", sub: "Productivity Suite" },
  { icon: "🤖", name: "Copilot AI", sub: "AI-Powered Work" },
  { icon: "🛡️", name: "Microsoft Security", sub: "Zero Trust Platform" },
  { icon: "🔗", name: "Teams & Viva", sub: "Collaboration" },
];

const m365Cards = [
  { icon: "💬", name: "Microsoft Teams", color: "linear-gradient(135deg,#4b53bc,#6264a7)", desc: "The hub for teamwork — meetings, calling, chat, file collaboration, and app integration in one platform trusted by 300M+ daily active users.", features: ["Teams Meetings & Webinars", "Teams Phone (Cloud Calling)", "Teams Rooms for Devices", "Teams Live Events"] },
  { icon: "📊", name: "Microsoft 365 Apps", color: "linear-gradient(135deg,#d83b01,#ea6017)", desc: "Word, Excel, PowerPoint, OneNote, and Outlook — now AI-enhanced with Microsoft Copilot, cloud collaboration, and real-time co-authoring for hybrid teams.", features: ["Microsoft Copilot Integration", "Real-Time Co-Authoring", "Cloud Storage via OneDrive", "Mobile & Web Access"] },
  { icon: "🔒", name: "Microsoft Intune", color: "linear-gradient(135deg,#0067b8,#0078d4)", desc: "Cloud-based unified endpoint management for every device your workforce uses — enforcing compliance, deploying apps, and protecting data without on-premises infrastructure.", features: ["Device Enrollment & Policy", "App Protection Policies", "Conditional Access Integration", "Autopilot Zero-Touch Deploy"] },
  { icon: "📧", name: "Exchange & SharePoint", color: "linear-gradient(135deg,#036c70,#17a589)", desc: "Enterprise email with Exchange Online and intranet/document management with SharePoint — the backbone of modern workplace information architecture.", features: ["Exchange Online Archiving", "SharePoint Sites & Lists", "Information Barriers", "Data Loss Prevention"] },
  { icon: "📈", name: "Power Platform", color: "linear-gradient(135deg,#742774,#9b4f96)", desc: "Low-code application development with Power Apps, process automation with Power Automate, and business intelligence with Power BI — all connected to your Microsoft data.", features: ["Power BI Embedded Analytics", "Power Apps Custom Dev", "Power Automate Workflows", "Power Virtual Agents"] },
  { icon: "🌐", name: "Microsoft Viva", color: "linear-gradient(135deg,#1b4a89,#0067b8)", desc: "Employee experience platform integrating learning, insights, goals, and engagement tools directly into Teams — supporting culture, wellbeing, and growth at scale.", features: ["Viva Insights (Wellbeing)", "Viva Learning", "Viva Goals (OKRs)", "Viva Connections"] },
];

const azureServices = [
  "Azure Virtual Machines", "Azure Kubernetes Service", "Azure App Service", "Azure SQL & Cosmos DB",
  "Azure Blob & Data Lake", "Azure AI & Cognitive Services", "Azure DevOps & GitHub Actions", "Azure Virtual Desktop",
];

const copilotCaps = [
  { icon: "✍️", title: "Copilot in Word & Outlook", desc: "Drafts emails, summarizes threads, and writes documents from a simple prompt — eliminating blank-page paralysis and reducing writing time by 50%+." },
  { icon: "📊", title: "Copilot in Excel & PowerPoint", desc: "Analyzes data and creates visualizations in Excel, builds slide decks from prompts in PowerPoint, and explains complex formulas in plain language." },
  { icon: "🎥", title: "Copilot in Teams Meetings", desc: "Joins meetings, takes notes, identifies action items, and answers questions about what was said — even if you joined late or missed the meeting entirely." },
  { icon: "💻", title: "GitHub Copilot for Developers", desc: "AI pair programmer that suggests code completions, writes tests, explains code, and fixes bugs directly in VS Code, Visual Studio, and JetBrains IDEs." },
];

const securityPillars = [
  { icon: "🔑", title: "Microsoft Entra ID (Azure AD)", desc: "Cloud identity platform managing sign-in for 600M+ users — with Conditional Access, MFA, Privileged Identity Management, and passwordless authentication." },
  { icon: "🛡️", title: "Microsoft Defender XDR", desc: "Unified extended detection and response across endpoints, identity, email, and cloud apps — correlating signals automatically with AI-powered investigation." },
  { icon: "🌐", title: "Microsoft Sentinel SIEM/SOAR", desc: "Cloud-native SIEM with unlimited scale, AI-driven threat detection, and automated playbooks that eliminate the alert fatigue of traditional SIEMs." },
  { icon: "🔒", title: "Microsoft Purview (Compliance)", desc: "Data governance, information protection, and compliance management — DLP, sensitivity labels, insider risk management, and eDiscovery in one platform." },
];

const hybridStats = [
  { val: "85%", label: "of MS 365 features work offline" },
  { val: "99.9%", label: "SLA for all M365 services" },
  { val: "300M+", label: "Teams daily active users" },
  { val: "180+", label: "Countries with Azure regions" },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant Microsoft 365, Teams for clinical collaboration, Azure Health Data Services for FHIR, and Nuance DAX for AI clinical documentation." },
  { icon: "🏦", name: "Financial Services", desc: "Azure for regulated financial workloads, Microsoft Purview for compliance, Teams for advisors, and Power BI for real-time risk dashboards." },
  { icon: "🎓", name: "Education", desc: "Microsoft 365 Education with Teams for virtual classrooms, Class Notebook, School Data Sync, and Azure Lab Services for student computing environments." },
  { icon: "🏛️", name: "Government", desc: "Microsoft 365 Government and Azure Government clouds — IL4 and IL5 authorized, FedRAMP High, and DoD Impact Level compliant infrastructure." },
  { icon: "🏭", name: "Manufacturing", desc: "Azure IoT Hub for connected factory, HoloLens for remote assist and guided work instructions, and Dynamics 365 for supply chain management." },
  { icon: "🛒", name: "Retail", desc: "Microsoft Cloud for Retail connecting inventory, omnichannel commerce, and personalized shopping experiences — powered by Azure and Teams Store Operations." },
  { icon: "⚡", name: "Energy", desc: "Azure energy-specific data platform, Power BI for operational analytics, and Microsoft Sentinel for OT/IT security monitoring in grid environments." },
  { icon: "🏗️", name: "Construction", desc: "Microsoft 365 for distributed field teams, Teams for project communication, Power Apps for inspection workflows, and Azure Maps for site management." },
];

const transformPillars = [
  { icon: "🤝", title: "Employee Experience", desc: "Microsoft Viva and Teams transform how employees connect, learn, and grow — reducing turnover through better engagement tools." },
  { icon: "📊", title: "Data-Driven Decisions", desc: "Power BI and Azure Synapse Analytics deliver business intelligence from every corner of your Microsoft data estate to every decision-maker." },
  { icon: "⚡", title: "Process Automation", desc: "Power Automate and Azure Logic Apps eliminate repetitive manual processes — freeing knowledge workers for higher-value activities." },
  { icon: "🔒", title: "Zero Trust Security", desc: "Microsoft Entra, Defender, and Purview deliver a complete zero-trust security posture across identity, devices, apps, and data." },
];

const certs = [
  { icon: "🏆", name: "Microsoft Gold Partner", desc: "Gold-tier Microsoft partner across Cloud Platform, Productivity, and Security competencies" },
  { icon: "☁️", name: "Azure Expert MSP", desc: "Microsoft Azure Expert Managed Service Provider — highest MSP designation for Azure operations" },
  { icon: "💼", name: "Microsoft 365 Specialist", desc: "Certified M365 deployment expertise across Teams, Exchange, SharePoint, and Intune" },
  { icon: "🛡️", name: "Security Competency", desc: "Microsoft Security advanced specialization for Defender, Sentinel, and Entra implementation" },
];

const faqData = [
  { q: "What is Microsoft 365 and how does it differ from traditional Office licenses?", a: "Microsoft 365 is a cloud subscription that combines the full Office application suite with enterprise cloud services not available in traditional perpetual licenses. Beyond Word, Excel, PowerPoint, and Outlook, Microsoft 365 includes Teams for collaboration and video calling, SharePoint for intranet and document management, OneDrive for cloud storage, Exchange Online for email, Intune for device management, Azure Active Directory (now Entra ID) for identity, and Microsoft Defender for security. The subscription model means organizations always have the latest versions, and all data is stored in Microsoft's SOC 2 Type II and ISO 27001 certified cloud infrastructure. ALG Data Guard manages the full Microsoft 365 deployment lifecycle from tenant provisioning through migration and ongoing operations." },
  { q: "What is Microsoft Copilot and what does it require to deploy?", a: "Microsoft Copilot for Microsoft 365 is an AI productivity assistant powered by OpenAI's large language models, grounded in your organization's Microsoft 365 data through Microsoft Graph. It integrates directly into Teams, Outlook, Word, Excel, PowerPoint, and other M365 applications — drafting content, summarizing meetings, analyzing data, and answering questions about your organizational information. To deploy Copilot, organizations require Microsoft 365 E3 or E5 licensing (or Business Premium/Standard equivalent), plus the Microsoft 365 Copilot add-on license. ALG Data Guard assists with Copilot readiness assessment, data governance prerequisites through Microsoft Purview, and phased user rollout with adoption training." },
  { q: "How does Microsoft Azure compare to AWS and Google Cloud for enterprise workloads?", a: "Microsoft Azure excels in three enterprise-specific areas: first, hybrid cloud integration — Azure Arc and Azure Stack extend Azure management and services to on-premises and multi-cloud environments better than any competing platform; second, Microsoft ecosystem integration — organizations running Windows Server, SQL Server, Active Directory, and Microsoft 365 get significant benefits from Azure's native integration with these workloads; third, enterprise identity and security — Azure Entra ID and Microsoft Defender for Cloud are the deepest cloud security integrations available. Azure offers significant cost benefits for Windows and SQL Server through Hybrid Benefit licensing, which can reduce VM costs by 40-55%. ALG Data Guard evaluates cloud platforms against your specific workload mix and existing Microsoft licensing." },
  { q: "What is Microsoft Sentinel and how does it improve security operations?", a: "Microsoft Sentinel is a cloud-native Security Information and Event Management (SIEM) and Security Orchestration, Automation, and Response (SOAR) platform built on Azure Log Analytics. Unlike traditional SIEM products that require expensive hardware and storage infrastructure, Sentinel scales to petabytes of data automatically and charges only for data ingested. Sentinel's AI-powered analytics uses Microsoft's global threat intelligence from 65 trillion daily signals to detect attacks that rules-based detection misses. Automated playbooks through Logic Apps respond to incidents in seconds — isolating endpoints, blocking IPs, or ticketing incidents — without analyst intervention. ALG Data Guard deploys Sentinel with pre-built detection rules, custom analytics aligned to your threat model, and integration with your existing security tools." },
  { q: "How does ALG Data Guard support Microsoft environments ongoing?", a: "ALG Data Guard provides comprehensive managed Microsoft services including: Microsoft 365 tenant administration and license optimization, Azure infrastructure monitoring and cost management, Microsoft Defender alert triage and incident response, Microsoft Sentinel SIEM monitoring with 24/7 SOC coverage, Teams and Exchange administration, Intune device policy management, compliance reporting for Microsoft Purview, and monthly security posture reviews. We also provide adoption services — training and change management to ensure your organization actually uses the capabilities you're paying for, which is the most overlooked aspect of Microsoft deployments." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Microsoft() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(1400, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(300, 2000, metricsInView);
  const c4 = useCounter(60, 2000, metricsInView);

  return (
    <div className="ms-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={microsoftLogo} alt="Microsoft" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Digital Workplace & Cloud Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Microsoft</h1>
            <p className="pd-identity-desc">
              Microsoft is the enterprise technology backbone for 95% of Fortune 500 companies — delivering Azure cloud infrastructure, Microsoft 365 productivity, Copilot AI, and the world's most comprehensive enterprise security platform. ALG Data Guard is a Microsoft Gold Partner with Azure Expert MSP designation, providing deployment, migration, and managed services across the full Microsoft portfolio.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="ms-hero">
        <div className="ms-hero__left">
          <div className="ms-hero__left-content">
            <div className="ms-hero__kicker">Microsoft Gold Partner — Azure Expert MSP</div>
            <h2 className="ms-hero__title">
              The Digital Workplace.<br /><em>Reimagined for What's Next.</em>
            </h2>
            <p className="ms-hero__body">
              From the cloud platform that powers half the world's enterprises to the AI assistant that's transforming how knowledge workers operate — Microsoft's ecosystem is the foundation of modern digital business. ALG Data Guard brings it all together for your organization.
            </p>
            <div className="ms-hero__actions">
              <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                Design My Microsoft Strategy <ArrowIcon />
              </button>
              <button className="ms-btn ms-btn--ghost-w" onClick={() => navigate("/contact")}>
                Talk to a Microsoft Expert
              </button>
            </div>
            <div className="ms-hero__stats">
              <div>
                <div className="ms-hero__stat-val">#1</div>
                <div className="ms-hero__stat-label">Enterprise Cloud Worldwide</div>
              </div>
              <div>
                <div className="ms-hero__stat-val">300M+</div>
                <div className="ms-hero__stat-label">Teams Users Monthly</div>
              </div>
              <div>
                <div className="ms-hero__stat-val">Gold</div>
                <div className="ms-hero__stat-label">ALG Data Guard Tier</div>
              </div>
            </div>
          </div>
        </div>
        <div className="ms-hero__right">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80"
            alt="Modern workplace collaboration"
            className="ms-hero__right-img"
          />
          <div className="ms-hero__right-overlay" aria-hidden="true" />
        </div>
      </section>

      {/* ── Capabilities Band ── */}
      <div className="ms-capabilities">
        <div className="ms-capabilities__inner">
          {capabilities.map(cap => (
            <div key={cap.name} className="ms-cap-item">
              <span className="ms-cap-item__icon">{cap.icon}</span>
              <div className="ms-cap-item__name">{cap.name}</div>
              <div className="ms-cap-item__sub">{cap.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modern Workplace ── */}
      <section className="ms-workplace">
        <div className="ms-shell">
          <div className="ms-workplace__inner">
            <div>
              <div className="ms-label ms-label--blue"><span className="ms-dot" /> Modern Workplace</div>
              <h2 className="ms-title">Where Great Work Happens — From Anywhere</h2>
              <p className="ms-sub" style={{ marginBottom:"1rem" }}>
                Microsoft has fundamentally transformed what a workplace means — making it a set of tools, experiences, and connections that follow workers across offices, homes, and mobile environments. Microsoft 365 is the platform that makes this possible for every organization, from 50-person firms to 500,000-employee enterprises.
              </p>
              <p className="ms-sub" style={{ marginBottom:"2rem" }}>
                ALG Data Guard manages your Microsoft 365 tenant from day one — migrating from legacy email and file systems, configuring security policies, and providing ongoing administration so your IT team focuses on strategic initiatives rather than platform management.
              </p>
              <div className="ms-workplace__apps">
                {["💬 Teams", "📧 Outlook", "📊 Excel", "📝 Word", "🔒 Intune", "📁 SharePoint", "☁️ OneDrive", "📈 Power BI", "🤖 Copilot"].map(app => (
                  <div key={app} className="ms-app-chip">
                    <span className="ms-app-chip__icon">{app.split(" ")[0]}</span>
                    <div className="ms-app-chip__name">{app.split(" ").slice(1).join(" ")}</div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=900&q=80"
              alt="Modern workplace collaboration"
              className="ms-workplace__img"
            />
          </div>
        </div>
      </section>

      {/* ── Azure Cloud ── */}
      <section className="ms-azure-section">
        <div className="ms-shell">
          <div className="ms-azure-section__inner">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="Microsoft Azure cloud infrastructure"
              className="ms-azure-section__img"
            />
            <div>
              <div className="ms-label ms-label--dark"><span className="ms-dot" /> Microsoft Azure</div>
              <h2 className="ms-azure-section__title">Azure — The Cloud Built for the Enterprise</h2>
              <p className="ms-azure-section__body">
                Microsoft Azure is the only cloud platform purpose-built for hybrid enterprise environments — integrating natively with your on-premises Active Directory, Windows Server, SQL Server, and Microsoft 365 data. With 60+ compliance certifications including FedRAMP High, HIPAA, PCI-DSS, and ISO 27001, Azure meets the regulatory requirements of every enterprise industry.
              </p>
              <p className="ms-azure-section__body">
                ALG Data Guard's Azure Expert MSP designation means we're one of Microsoft's most validated Azure operations partners — managing Azure environments for enterprise clients with 24/7 monitoring, cost optimization, and security governance.
              </p>
              <div className="ms-azure-services">
                {azureServices.map(s => (
                  <div key={s} className="ms-azure-service">☁️ {s}</div>
                ))}
              </div>
              <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                Design My Azure Architecture <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Microsoft 365 ── */}
      <section className="ms-m365">
        <div className="ms-shell">
          <div className="ms-m365__head">
            <div className="ms-label ms-label--blue"><span className="ms-dot" /> Microsoft 365</div>
            <h2 className="ms-title">The Complete Productivity & Collaboration Suite</h2>
            <p className="ms-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>
              Microsoft 365 is no longer just Office. It's the integrated productivity, collaboration, device management, and security platform that modern organizations run on.
            </p>
          </div>
          <div className="ms-m365__grid">
            {m365Cards.map(card => (
              <div key={card.name} className="ms-m365-card">
                <div className="ms-m365-card__header" style={{ background:card.color }}>
                  <span className="ms-m365-card__icon">{card.icon}</span>
                  <div className="ms-m365-card__name">{card.name}</div>
                </div>
                <div className="ms-m365-card__body">
                  <p className="ms-m365-card__desc">{card.desc}</p>
                  <ul className="ms-m365-card__features">{card.features.map(f => <li key={f}>{f}</li>)}</ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Copilot AI ── */}
      <section className="ms-copilot">
        <div className="ms-shell">
          <div className="ms-copilot__inner">
            <div>
              <div className="ms-label ms-label--dark"><span className="ms-dot" /> Microsoft Copilot</div>
              <h2 className="ms-copilot__title">AI Built Into Every Workflow</h2>
              <p className="ms-copilot__body">
                Microsoft Copilot is the most integrated enterprise AI assistant in the world — embedded directly into the applications your teams already use every day. Unlike standalone AI tools, Copilot understands your organization's data through Microsoft Graph, providing context-aware assistance that's both powerful and private.
              </p>
              <div className="ms-copilot__caps">
                {copilotCaps.map(cap => (
                  <div key={cap.title} className="ms-copilot-cap">
                    <span className="ms-copilot-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="ms-copilot-cap__title">{cap.title}</div>
                      <p className="ms-copilot-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"1.5rem" }}>
                <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                  Get Copilot Ready <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="Microsoft Copilot AI"
              className="ms-copilot__img"
            />
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      <section className="ms-security">
        <div className="ms-shell">
          <div className="ms-security__inner">
            <div>
              <div className="ms-label ms-label--blue"><span className="ms-dot" /> Microsoft Security</div>
              <h2 className="ms-title">The Most Comprehensive Enterprise Security Platform</h2>
              <p className="ms-sub" style={{ marginBottom:"0.5rem" }}>
                Microsoft invests $4 billion annually in cybersecurity research and development — and delivers that investment directly to customers through an integrated security portfolio that covers identity, endpoints, email, cloud, and compliance with unprecedented depth.
              </p>
              <div className="ms-security__pillars">
                {securityPillars.map(p => (
                  <div key={p.title} className="ms-security-pillar">
                    <span className="ms-security-pillar__icon">{p.icon}</span>
                    <div>
                      <div className="ms-security-pillar__title">{p.title}</div>
                      <p className="ms-security-pillar__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                  Assess My Security Posture <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80"
              alt="Microsoft security operations"
              className="ms-security__img"
            />
          </div>
        </div>
      </section>

      {/* ── Hybrid Workforce ── */}
      <section className="ms-hybrid">
        <div className="ms-shell">
          <div className="ms-hybrid__inner">
            <img
              src="https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=900&q=80"
              alt="Hybrid workforce collaboration"
              className="ms-hybrid__img"
            />
            <div>
              <div className="ms-label ms-label--blue"><span className="ms-dot" /> Hybrid Work</div>
              <h2 className="ms-title">Equip Your Workforce to Excel — Anywhere</h2>
              <p className="ms-sub" style={{ marginBottom:"0.5rem" }}>
                The modern workforce is distributed across offices, homes, and mobile environments. Microsoft 365 delivers a consistent, productive, and secure experience for every employee — regardless of where or how they work.
              </p>
              <div className="ms-hybrid__stats">
                {hybridStats.map(s => (
                  <div key={s.val} className="ms-hybrid-stat">
                    <div className="ms-hybrid-stat__val">{s.val}</div>
                    <div className="ms-hybrid-stat__label">{s.label}</div>
                  </div>
                ))}
              </div>
              <p className="ms-sub" style={{ marginBottom:"1.5rem" }}>
                ALG Data Guard manages Microsoft Teams Phone deployments — replacing on-premises PBX systems with cloud calling, and configuring Intune device compliance policies that protect corporate data without sacrificing the employee experience.
              </p>
              <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                Modernize My Workforce <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="ms-industries">
        <div className="ms-shell">
          <div className="ms-industries__head">
            <div className="ms-label ms-label--dark"><span className="ms-dot" /> Industry Solutions</div>
            <h2 className="ms-title">Microsoft Across Every Enterprise Vertical</h2>
            <p className="ms-sub">Microsoft's industry cloud solutions combine core M365 and Azure with industry-specific data models, workflows, and compliance configurations.</p>
          </div>
          <div className="ms-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="ms-industry-card">
                <span className="ms-industry-card__icon">{ind.icon}</span>
                <h3 className="ms-industry-card__name">{ind.name}</h3>
                <p className="ms-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Digital Transformation ── */}
      <section className="ms-transform">
        <div className="ms-shell">
          <div className="ms-transform__inner">
            <div>
              <div className="ms-label ms-label--blue"><span className="ms-dot" /> Digital Transformation</div>
              <h2 className="ms-title">Microsoft as the Foundation of Your Digital Future</h2>
              <p className="ms-sub" style={{ marginBottom:"0.5rem" }}>
                Digital transformation isn't a technology project — it's a fundamental change in how your organization creates and delivers value. Microsoft's integrated platform provides the building blocks; ALG Data Guard helps you use them strategically.
              </p>
              <div className="ms-transform__pillars">
                {transformPillars.map(p => (
                  <div key={p.title} className="ms-transform-pillar">
                    <span className="ms-transform-pillar__icon">{p.icon}</span>
                    <div className="ms-transform-pillar__title">{p.title}</div>
                    <p className="ms-transform-pillar__desc">{p.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
                  Start My Transformation <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80"
              alt="Digital transformation"
              className="ms-transform__img"
            />
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="ms-metrics" ref={metricsRef}>
        <div className="ms-shell">
          <div className="ms-metrics__inner">
            <div><div className="ms-metric__val">{c1}+</div><div className="ms-metric__label">Microsoft Licenses Managed</div></div>
            <div><div className="ms-metric__val">{c2}.9%</div><div className="ms-metric__label">Azure Uptime SLA</div></div>
            <div><div className="ms-metric__val">{c3}M+</div><div className="ms-metric__label">Teams Active Users Globally</div></div>
            <div><div className="ms-metric__val">{c4}+</div><div className="ms-metric__label">Azure Regions Available</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="ms-certs">
        <div className="ms-shell">
          <div className="ms-certs__head">
            <div className="ms-label ms-label--blue"><span className="ms-dot" /> Partner Credentials</div>
            <h2 className="ms-title">Certified Microsoft Gold Partner</h2>
            <p className="ms-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's Microsoft Gold certifications and Azure Expert MSP designation validate our depth of expertise across the Microsoft platform.</p>
          </div>
          <div className="ms-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="ms-cert-card">
                <span className="ms-cert-card__icon">{c.icon}</span>
                <div className="ms-cert-card__name">{c.name}</div>
                <p className="ms-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="ms-faq">
        <div className="ms-shell">
          <div className="ms-faq__head">
            <div className="ms-label ms-label--dark"><span className="ms-dot" /> FAQs</div>
            <h2 className="ms-title">Microsoft Questions, Answered</h2>
            <p className="ms-sub">Expert answers about Microsoft 365, Azure, Copilot, and ALG Data Guard's managed services.</p>
          </div>
          <div className="ms-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`ms-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="ms-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="ms-faq-item__q">{faq.q}</span>
                  <span className="ms-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="ms-faq-item__answer"><div className="ms-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ms-cta">
        <div className="ms-cta__inner">
          <div className="ms-label ms-label--dark" style={{ margin:"0 auto 1.2rem" }}><span className="ms-dot" /> Get Started</div>
          <h2 className="ms-cta__title">Unlock the Full Microsoft Ecosystem for Your Business</h2>
          <p className="ms-cta__sub">
            From Microsoft 365 migration and Azure cloud architecture to Copilot AI rollout and 24/7 managed security — ALG Data Guard's Microsoft Gold-certified team delivers the outcomes your digital transformation demands.
          </p>
          <div className="ms-cta__actions">
            <button className="ms-btn ms-btn--primary" onClick={() => navigate("/contact")}>
              Request a Microsoft Assessment <ArrowIcon />
            </button>
            <button className="ms-btn ms-btn--ghost-w" onClick={() => navigate("/contact")}>
              Talk to a Microsoft Expert
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}